import { merge } from '@/helpers/Utility';
import { dateToApiFormat, isDateInPast, localDateToUtcDate } from '@/helpers/dateHelper';
import { FC, useEffect, useMemo } from 'react';
import { useLilius } from 'use-lilius';
import { CalendarHeader } from './CalendarHeader';
import calendarTranslations from './calendar.translations';
import { CalendarProps } from './calendar.types';

export const Calendar: FC<CalendarProps> = ({
  monthToShow,
  events,
  ContentComponent,
  options = {},
  weeksStartsOn = 1,
  className
}) => {
  const utcAdjustedDate = useMemo(() => {
    // Adjust the date to be the third of the month. This means we won't have any awkward timezone shits.
    return new Date(monthToShow.getUTCFullYear(), monthToShow.getUTCMonth(), 3);
  }, [monthToShow]);

  const lilius = useLilius({ viewing: utcAdjustedDate, weekStartsOn: weeksStartsOn });
  const weeks = lilius.calendar[0];
  const daysOfWeek = Object.values(calendarTranslations.shorthandDays);

  useEffect(() => {
    lilius.setViewing(utcAdjustedDate);
  }, [utcAdjustedDate, lilius]);

  return (
    <div className={merge(className, 'flex flex-col overflow-hidden h-full w-full border bg-primary-std rounded-lg')}>
      <div className="flex w-full border-b border-l border-r">
        {weeks[0].map(day => (
          <div className="flex flex-col w-full items-center py-2 font-bold text-gray-500" key={day.getDate()}>
            {daysOfWeek[day.getDay()]}
          </div>
        ))}
      </div>
      {weeks.map((currentWeek: Date[], idx: number) => {
        return (
          <div className="flex flex-1 relative" key={idx}>
            {currentWeek.map(currentDay => {
              const currentKey = dateToApiFormat(localDateToUtcDate(currentDay));
              const event = events?.find(e => dateToApiFormat(e.date) === currentKey);
              const isToday = currentDay.toDateString() === new Date().toDateString();
              const greyMonth = currentDay.getMonth() !== lilius.viewing.getMonth();

              const isOverflow = greyMonth;

              return (
                <div
                  key={currentDay.getDate()}
                  className={`flex-1 border-primary-std border flex flex-col relative ${
                    event?.contextObject.onClick ? 'cursor-pointer' : ''
                  }`}
                  style={{ minHeight: options?.minimumCellHeight || 100 }}
                  onClick={event?.contextObject.onClick}
                >
                  {isOverflow && <div className="absolute inset-0 bg-gray-300 bg-opacity-30" />}

                  {(!isOverflow || !options?.hideOverflow) && (
                    <>
                      <CalendarHeader date={currentDay} />

                      <div className={`${isDateInPast(currentDay, new Date()) ? 'opacity-50' : ''} flex-1`}>
                        <ContentComponent date={currentDay} contextObject={event?.contextObject} today={isToday} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
